import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { useTranslation } from "react-i18next";
import { WithAuth } from "../../components/Authorization/WithAuth";
import Card from "../../components/Cards/Card";
import { Add, Next, ViewProtocol } from "../../components/Icons/Icons";
import { Tooltip } from "../../components/Utils/Tooltips";
import { useClinicalTrials, useMasterData } from "./Hooks";
import { ClinicalTrialsFilters } from "../../components/ClinicalTrials/ClinicalTrialsFilters";
import { NavLink } from "react-router-dom";
import { useAuthorization } from "../../components/Authorization/useAuthorization";
import { getStartupsSpecialties } from "../../api/clinicalTrial";
import { HandleApiError } from "../../components/Notifications/APIErrorHandler";

export const ClinicalTrials = () => {
  const { t } = useTranslation();
  const [, , isAuthorized] = useAuthorization();

  const [masterData, isLoadingMasterData] = useMasterData();
  const [startupsSpecialties, setStartupsSpecialties] = useState([]);
  const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false);
  const [
    data,
    query,
    totalSize,
    isLoading,
    handleTableChange,
    handleFilterChange,
  ] = useClinicalTrials();

  const columns = [
    {
      dataField: "laboratory",
      text: t("clinicalTrials.startup.sponsor"),
      headerStyle: { width: "100px" },
      formatter: (cell) => cell?.title ?? "-",
    },
    {
      dataField: "protocol.title",
      text: t("clinicalTrials.startup.acronym"),
      headerStyle: { width: "200px" },
    },
    {
      dataField: "protocol.specialty.description",
      text: t("clinicalTrials.startup.specialty"),
      sort: true,
      headerStyle: { width: "200px" },
    },
    {
      dataField: "indication.description",
      text: t("clinicalTrials.brief.associatedIndications"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "keywords",
      text: t("clinicalTrials.startup.keywords"),
      headerStyle: { width: "100px" },
      formatter: (keywords) => {
        if (!Array.isArray(keywords) || keywords.length === 0) return "-";
        return keywords.map((k) => k.description).join(", ");
      },
    },
    {
      dataField: "principalInvestigator",
      text: t("clinicalTrials.startup.principalInvestigator"),
      headerStyle: { width: "100px" },
      formatter: (cell) => cell?.fullName ?? "-",
    },
    {
      dataField: "status",
      text: t("clinicalTrials.startup.status"),
      headerStyle: { width: "100px" },
      formatter: (_cell, row) =>
        row?.status?.code
          ? t(`clinicalTrials.statuses.${row.status.code}`)
          : row?.status?.title ?? "-",
    },
    {
      dataField: "version",
      text: t("clinicalTrials.startup.version"),
      headerStyle: { width: "50px" },
    },
    {
      dataField: "view",
      text: "",
      isDummyField: true,
      headerClasses: "header-protocol-actions",
      classes: "row-protocol-actions",
      headerStyle: { width: "100px" },
      headerAlign: "center",
      align: "center",
      formatter: (_cell, row, rowIndex) => (
        <div key={rowIndex}>
          {isAuthorized(["Protocols.View"]) &&
            (row.status?.code === "Recruiting" ||
              row.status?.code === "OnGoing" ||
              row.status?.code === "Closed") && (
              <Tooltip
                tooltip={t("protocols.visitTracking.viewProtocol")}
                id={"tooltipconfig-" + row.id}
              >
                <NavLink
                  to={`/admin/protocols/${row.protocol.id}`}
                  activeClassName="active"
                  className="btn-table-actions"
                >
                  <ViewProtocol />
                </NavLink>
              </Tooltip>
            )}

          {isAuthorized(["Startups.View"]) && (
            <Tooltip tooltip="Ver más" id={`tooltipvisit-${row.id}`}>
              <NavLink
                to={`/admin/clinicalTrials/edit/${row.id}`}
                activeClassName="active"
                className="btn-table-actions"
              >
                <Next />
              </NavLink>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    let isSubscribed = true;
    const fetchSpecialties = async () => {
      setIsLoadingSpecialties(true);
      try {
        const response = await getStartupsSpecialties();
        if (isSubscribed && response.data) {
          // Agregar propiedad title para el componente CustomSelect y eliminar duplicados por description
          const seen = new Map();
          const specialtiesWithTitle = response.data
            .map(s => ({
              ...s,
              title: s.description
            }))
            .filter(s => {
              const descriptionKey = s.description?.toLowerCase() || '';
              if (seen.has(descriptionKey)) {
                return false;
              }
              seen.set(descriptionKey, true);
              return true;
            });
          setStartupsSpecialties(specialtiesWithTitle);
        }
      } catch (error) {
        if (isSubscribed) {
          HandleApiError(error);
        }
      } finally {
        if (isSubscribed) {
          setIsLoadingSpecialties(false);
        }
      }
    };

    fetchSpecialties();

    return () => {
      isSubscribed = false;
    };
  }, []);

  return (
    <>
      <Card
        header={
          <Row>
            <Col md={2}>
              <WithAuth requiredPermission={["Protocols.Create"]}>
                <NavLink
                  to="/admin/clinicalTrials/create"
                  className="btn btn-primary"
                  activeClassName="active"
                >
                  <Add /> {t("clinicalTrials.brief.addClinicalTrial")}
                </NavLink>
              </WithAuth>
            </Col>
            <ClinicalTrialsFilters
              isLoading={isLoadingMasterData || isLoadingSpecialties}
              masterData={masterData}
              onChangeFilter={handleFilterChange}
              selectedLaboratory={masterData.Laboratories?.find(l => l.id === query.filters?.laboratoryId)}
              selectedStatus={masterData.Status?.find(s => s.id === query.filters?.statusId)}
              selectedSpecialty={startupsSpecialties.find(s => s.id === query.filters?.specialtyId)}
              startupsSpecialties={startupsSpecialties}
            />
          </Row>
        }
        content={
          <BootstrapTable
            bordered={false}
            hover
            remote
            keyField="id"
            data={data}
            columns={columns}
            classes="table-spaced table-spaced-small"
            wrapperClasses="table-responsive"
            pagination={paginationFactory({
              page: query.pagination.page,
              sizePerPage: query.pagination.sizePerPage,
              totalSize: totalSize,
              hideSizePerPage: true,
            })}
            onTableChange={handleTableChange}
            noDataIndication={() => <>{t("commons.noData")}</>}
            loading={isLoading}
            overlay={overlayFactory({ spinner: true })}
          />
        }
      />
    </>
  );
};
